import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Agent, AGENT_PERMISSIONS, channelLabel, Conversation, ProjectOption } from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';

@Component({
  selector: 'app-ai-conversations-page',
  templateUrl: './conversations-page.component.html',
  styleUrls: ['../../ai-agents.shared.css'],
})
export class ConversationsPageComponent implements OnInit {
  readonly P = AGENT_PERMISSIONS;
  /** Contract v1.6: TRIGGER is shown as "Automation". */
  readonly channelLabel = channelLabel;
  readonly sizes = [20, 50, 100];

  projects: ProjectOption[] = [];
  agents: Agent[] = [];
  conversations: Conversation[] = [];
  total = 0;
  page = 0;
  size = 20;
  projectId = '';
  agentId = '';
  loading = false;

  constructor(
    private api: AiAgentsApiService,
    public perms: AgentPermissionsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap;
    this.projectId = q.get('projectId') || '';
    this.agentId = q.get('agentId') || '';
    this.page = Math.max(0, Number(q.get('page') || 0) || 0);
    this.api.listProjects().subscribe({ next: (list) => (this.projects = list), error: () => (this.projects = []) });
    this.loadAgents();
    this.load();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  get agentOptions(): Agent[] {
    return this.projectId ? this.agents.filter((a) => a.projectId === this.projectId) : this.agents;
  }

  agentName(id: string): string {
    return this.agents.find((a) => a.id === id)?.name || id;
  }

  projectName(id: string): string {
    return this.projects.find((p) => p.projectId === id)?.name || id;
  }

  loadAgents(): void {
    if (!this.perms.has(this.P.agentList)) return;
    this.api.listAgents().subscribe({ next: (list) => (this.agents = list || []) });
  }

  onProjectChange(): void {
    if (this.agentId && !this.agentOptions.some((a) => a.id === this.agentId)) {
      this.agentId = '';
    }
    this.page = 0;
    this.applyFilters();
  }

  applyFilters(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { projectId: this.projectId || null, agentId: this.agentId || null, page: this.page || null },
      replaceUrl: true,
    });
    this.load();
  }

  goTo(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.page = page;
    this.applyFilters();
  }

  load(): void {
    this.loading = true;
    this.api
      .listConversations({ projectId: this.projectId || null, agentId: this.agentId || null, page: this.page, size: this.size })
      .subscribe({
        next: (res) => {
          this.conversations = res?.items || [];
          this.total = res?.total || 0;
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  open(c: Conversation): void {
    if (this.perms.has(this.P.conversationGet)) {
      this.router.navigate(['/ai-agents/conversations', c.id]);
    }
  }
}
